import React from 'react'
import { Text } from 'shared/components/Text/Text'

import { Handler } from '../../../helpers/typeHelper'
import {
  cancelDeductionCredit,
  DeductionConfigStatus,
  DeductionCreditReason,
  Repayment,
} from '../../../models/repayments'
import { midGray } from '../../../theme/colors'
import { PolicyCorrectionDetails } from './PolicyCorrectionDetails'
import { PolicyPreviousDetails } from './PolicyPreviousDetails'
import { PolicyRefinanceDetails } from './PolicyRefinanceDetails'

interface PolicyActivityProps {
  policy: Repayment
  refetchPolicy: Handler
}

export function PolicyActivity({ policy, refetchPolicy }: PolicyActivityProps) {
  const defaultDeductionConfig = policy?.repayment_details.deduction_configs.find(
    (config) => config.reason === DeductionCreditReason.DEFAULT
  )
  const scheduledCorrectionConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.CORRECTION &&
        config.status === DeductionConfigStatus.SCHEDULED
    ) ?? []
  const activeCorrectionConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.CORRECTION && config.status === DeductionConfigStatus.ACTIVE
    ) ?? []
  const finishedCorrectionConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.CORRECTION && config.status === DeductionConfigStatus.FINISHED
    ) ?? []
  const scheduledRefinanceConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.REFINANCE && config.status === DeductionConfigStatus.SCHEDULED
    ) ?? []
  const activeRefinanceConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.REFINANCE && config.status === DeductionConfigStatus.ACTIVE
    ) ?? []
  const finishedRefinanceConfigs =
    policy?.repayment_details.deduction_configs.filter(
      (config) =>
        config.reason === DeductionCreditReason.REFINANCE && config.status === DeductionConfigStatus.FINISHED
    ) ?? []

  const onCancelConfig = async (id: string) => {
    return cancelDeductionCredit(id).then(() => refetchPolicy())
  }

  const hasContent =
    scheduledCorrectionConfigs.length ||
    activeCorrectionConfigs.length ||
    finishedCorrectionConfigs.length ||
    scheduledRefinanceConfigs.length ||
    activeRefinanceConfigs.length ||
    finishedRefinanceConfigs.length

  if (!hasContent) return null

  return (
    <>
      <Text variant="uppercaseLabel2" color={midGray} mb={2}>
        Change history
      </Text>

      {!!scheduledCorrectionConfigs.length && (
        <>
          {scheduledCorrectionConfigs.map((deductionConfig, idx) => (
            <PolicyCorrectionDetails
              key={idx}
              policy={policy}
              defaultDeductionConfig={defaultDeductionConfig}
              config={deductionConfig}
              onCancelConfig={onCancelConfig}
            />
          ))}
        </>
      )}

      {!!activeCorrectionConfigs.length && (
        <>
          {activeCorrectionConfigs.map((deductionConfig, idx) => (
            <PolicyCorrectionDetails
              key={idx}
              policy={policy}
              defaultDeductionConfig={defaultDeductionConfig}
              config={deductionConfig}
              onCancelConfig={onCancelConfig}
            />
          ))}
        </>
      )}

      {!!finishedCorrectionConfigs.length && (
        <>
          {finishedCorrectionConfigs.map((deductionConfig, idx) => (
            <PolicyCorrectionDetails
              key={idx}
              policy={policy}
              defaultDeductionConfig={defaultDeductionConfig}
              config={deductionConfig}
              onCancelConfig={onCancelConfig}
            />
          ))}
        </>
      )}

      {!!scheduledRefinanceConfigs.length && (
        <>
          {scheduledRefinanceConfigs.map((refinanceConfig, idx) => (
            <PolicyRefinanceDetails
              key={idx}
              policy={policy}
              config={refinanceConfig}
              onCancelConfig={onCancelConfig}
            />
          ))}
        </>
      )}

      {!!activeRefinanceConfigs.length && (
        <>
          {activeRefinanceConfigs.map((refinanceConfig, idx) => (
            <PolicyPreviousDetails key={idx} policy={policy} config={refinanceConfig} />
          ))}
        </>
      )}

      {!!finishedRefinanceConfigs.length && (
        <>
          {finishedRefinanceConfigs.map((refinanceConfig, idx) => (
            <PolicyPreviousDetails key={idx} policy={policy} config={refinanceConfig} />
          ))}
        </>
      )}
    </>
  )
}
